/**
 * Loyalty-account region from the ID token's `custom:userCountry` claim
 * (THLE-4256), the same attribute the backend gate reads (THLE-4044). Note the
 * 2-letter form, unlike `me.details.isoCountryCode` (`CAN`/`USA`), and that it
 * describes the account rather than `useLocale().region`, which is where the
 * guest is browsing.
 *
 * SECURITY: a browser-decoded JWT is forgeable, so this drives presentation
 * only. `REGION_NOT_ELIGIBLE` from `initiateLinking` is the real gate; reading
 * the claim only avoids a round-trip we already know will be refused.
 */

import { parseJwt } from 'utils/jwt';

export type AccountRegion = 'CA' | 'US';

const ACCOUNT_REGION_CLAIM = 'custom:userCountry';

/**
 * `null` when the claim is absent or unrecognized, meaning "unknown" — callers
 * must fail open and let the backend decide rather than blocking the guest.
 */
export function getAccountRegionFromClaims(
  claims: Record<string, unknown> | null | undefined
): AccountRegion | null {
  const claim = claims?.[ACCOUNT_REGION_CLAIM];
  return claim === 'CA' || claim === 'US' ? claim : null;
}

/** As above, for a raw JWT string such as the app handoff token. */
export function getAccountRegion(idToken: string | null): AccountRegion | null {
  if (!idToken) {
    return null;
  }
  return getAccountRegionFromClaims(parseJwt(idToken));
}
